import { useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { useMembers } from '@/features/members/queries';
import { collectionMembersHooks, collectionsHooks } from '../queries';
import type {
  WikiCollection,
  WikiCollectionMember,
  WikiCollectionRole,
  WikiVisibility,
} from '../types';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

export function CollectionDialog({
  collection,
  open,
  onOpenChange,
  onCreated,
}: {
  /** null → create mode. */
  collection: WikiCollection | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: (c: WikiCollection) => void;
}) {
  const create = collectionsHooks.useCreate();
  const update = collectionsHooks.useUpdate();
  const editing = collection !== null;

  // Initialised from `collection`; the host remounts this dialog (via `key`) per
  // target + open, so these initializers re-run with the right values.
  const [name, setName] = useState(collection?.name ?? '');
  const [icon, setIcon] = useState(collection?.icon ?? '');
  const [visibility, setVisibility] = useState<WikiVisibility>(collection?.visibility ?? 'public');

  function submit() {
    if (!name.trim()) return;
    const input = { name: name.trim(), icon, visibility };
    if (editing) {
      update.mutate(
        { id: collection.public_id, input },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create.mutate(input, {
        onSuccess: (c) => {
          onCreated?.(c);
          onOpenChange(false);
          setName('');
          setIcon('');
          setVisibility('public');
        },
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Collection settings' : 'New collection'}</DialogTitle>
          <DialogDescription>
            Collections group related pages. Private collections are visible only to their members.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: DialogContent is a CSS grid — let this block shrink so long
            member names truncate instead of overflowing the modal. */}
        <div className="min-w-0 space-y-3">
          <div className="flex items-end gap-2">
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <EmojiPicker compact value={icon} onChange={setIcon} placeholder="Icon" />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="collection-name">Name</Label>
              <Input
                id="collection-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Engineering"
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Visibility</Label>
            <Select value={visibility} onValueChange={(v) => setVisibility(v as WikiVisibility)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public — all staff can read</SelectItem>
                <SelectItem value="private">Private — members only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {editing && collection ? <CollectionMembers collection={collection} /> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending || update.isPending}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollectionMembers({ collection }: { collection: WikiCollection }) {
  const membersQuery = collectionMembersHooks.useList({ collection: collection.id });
  const add = collectionMembersHooks.useCreate();
  const updateRole = collectionMembersHooks.useUpdate();
  const remove = collectionMembersHooks.useRemove();
  const [search, setSearch] = useState('');
  const orgMembers = useMembers({ search: search || undefined, type: 'staff', is_active: true });

  const members = rows<WikiCollectionMember>(membersQuery.data);
  const existing = new Set(members.map((m) => m.member));
  const candidates = rows(orgMembers.data).filter((m) => !existing.has(m.id));

  return (
    <div className="space-y-2 border-t pt-3">
      <Label>Members</Label>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Add teammate…"
        className="h-9"
      />
      {search ? (
        <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border p-1">
          {orgMembers.isPending ? (
            <Skeleton className="h-12 w-full" />
          ) : candidates.length === 0 ? (
            <p className="text-muted-foreground px-2 py-2 text-xs">No matching teammates.</p>
          ) : (
            candidates.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={add.isPending}
                onClick={() =>
                  add.mutate(
                    { collection: collection.id, member: m.id, role: 'viewer' },
                    { onSuccess: () => setSearch('') },
                  )
                }
                className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
              >
                <UserPlus className="text-muted-foreground size-4" />
                <span className="min-w-0 truncate">{m.user.full_name || m.user.email}</span>
              </button>
            ))
          )}
        </div>
      ) : null}

      {membersQuery.isPending ? (
        <Skeleton className="h-12 w-full" />
      ) : members.length === 0 ? (
        <p className="text-muted-foreground py-1 text-sm">No members yet.</p>
      ) : (
        <ul className="divide-y">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="min-w-0 flex-1 truncate">{m.member_name || `Member #${m.member}`}</span>
              <Select
                value={m.role}
                onValueChange={(v) =>
                  updateRole.mutate({ id: m.id, input: { role: v as WikiCollectionRole } })
                }
              >
                <SelectTrigger className="h-7 w-24 shrink-0 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                aria-label="Remove member"
                onClick={() => remove.mutate(m.id)}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
